import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useReveal } from '../../hooks/useReveal'
import { API_BASE } from '../../config/api'

interface CaseStudy {
  id: string
  client: string
  description: string
  resultValue: string
  resultLabel: string
}

const defaultCases: CaseStudy[] = [
  {
    id: 'nova-retail',
    client: 'Nova Retail',
    description: 'E-commerce platform redesign with integrated analytics dashboard',
    resultValue: '+38%',
    resultLabel: 'conversion rate',
  },
  {
    id: 'finedge',
    client: 'FinEdge',
    description: 'High-availability financial trading platform with real-time data',
    resultValue: '99.99%',
    resultLabel: 'uptime',
  },
  {
    id: 'medicare-plus',
    client: 'MediCare+',
    description: 'Cross-platform health monitoring application with AI diagnostics',
    resultValue: '4.8 ★',
    resultLabel: 'app store rating',
  },
]

interface ClientWorkProps {
  cases?: CaseStudy[]
}

export default function ClientWork({ cases = defaultCases }: ClientWorkProps) {
  const [liveCases, setLiveCases] = useState<CaseStudy[]>(cases)
  const { ref, isVisible } = useReveal<HTMLDivElement>()

  useEffect(() => {
    async function fetchCases() {
      try {
        const res = await fetch(`${API_BASE}/api/v1/client-work`)
        if (!res.ok) throw new Error('API failed')
        const json = await res.json()
        if (json.success && Array.isArray(json.data) && json.data.length > 0) {
          setLiveCases(json.data)
        }
      } catch (err) {
        console.error('Failed to fetch client work cases:', err)
      }
    }
    fetchCases()
  }, [])

  return (
    <section className="section-spacing" id="work">
      <div className="container-page">
        <div className="section-head">
          <span className="eyebrow">Client Work</span>
          <h2 className="mt-4" style={{ color: 'var(--text)' }}>Results that speak for themselves</h2>
          <p className="mt-4 text-[1.06rem]" style={{ color: 'var(--muted)' }}>
            Selected case studies from our client partnerships.
          </p>
        </div>

        <div
          ref={ref}
          className="rounded-card border overflow-hidden transition-all duration-700"
          style={{
            borderColor: 'var(--border)',
            background: 'var(--surface)',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          }}
        >
          {liveCases.map((c) => (
            <Link key={c.id} to={`/case-studies/${c.id}`} className="client-row no-underline" style={{ color: 'inherit' }}>
              <div className="font-display text-[1.12rem] font-semibold" style={{ color: 'var(--text)' }}>
                {c.client}
              </div>
              <div className="text-sm" style={{ color: 'var(--muted)' }}>
                {c.description}
              </div>
              <div className="text-right client-result">
                <div className="font-display text-[1.5rem] font-semibold" style={{ color: 'var(--text)', letterSpacing: '-.02em' }}>
                  {c.resultValue}
                </div>
                <div className="text-xs" style={{ color: 'var(--muted)' }}>
                  {c.resultLabel}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}
